import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useMandates, useDashboardMetrics } from "@/hooks/use-transactions";
import { useExecutiveMandateHealth } from "@/hooks/use-executive-demo-data";
import { RealtimeStatusIndicator } from "@/components/realtime/realtime-status-indicator";
import { Skeleton } from "@/components/ui/skeleton";

interface MandateInfo {
  initials: string;
  name: string;
  description: string;
  status: "success" | "warning" | "danger";
  bgColor: string;
  automationRate?: number;
  pendingCount?: number;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "success": return "bg-success";
    case "warning": return "bg-warning";
    case "danger": return "bg-danger";
    default: return "bg-muted";
  }
};

const getBgColorForStatus = (status: string, index: number) => {
  const colors = [
    "bg-brand-accent text-brand-accent-foreground",
    "bg-brand-primary text-brand-primary-foreground",
    "bg-brand-secondary border border-border text-brand-primary"
  ];
  return colors[index % colors.length];
};

const getInitials = (name: string) => {
  return name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase();
};

interface MandateHealthProps {
  demoMode?: boolean;
}

export const MandateHealth: React.FC<MandateHealthProps> = ({ demoMode = true }) => {
  const { data: demoData, isLoading: demoLoading } = useExecutiveMandateHealth();
  const { data: mandateData, isLoading: mandatesLoading, refetch: refetchMandates } = useMandates();
  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useDashboardMetrics();

  const isLoading = demoMode ? demoLoading : (mandatesLoading || metricsLoading);

  // Generate mandates based on demo or real data
  const getMandatesData = (): MandateInfo[] => {
    // Demo mode: use demo mandate health data
    if (demoMode && demoData) {
      return demoData.slice(0, 3).map((mandate, index) => ({
        initials: mandate.initials,
        name: mandate.name,
        description: `${mandate.type} • ${mandate.pending} pending`,
        status: mandate.status === 'excellent' || mandate.status === 'good' ? "success" as const :
                mandate.status === 'attention' ? "warning" as const : "danger" as const,
        bgColor: getBgColorForStatus(mandate.status, index),
        automationRate: mandate.confidence,
        pendingCount: mandate.pending,
      }));
    }

    // Real mode: use database data
    if (!mandateData || !metrics) return [];

    // Show real mandate data if available
    if (mandateData.length > 0) {
      return mandateData.slice(0, 3).map((mandate, index) => ({
        initials: getInitials(mandate.client_name),
        name: mandate.client_name,
        description: `${mandate.mandate_type.replace('_', ' ')} • ${mandate.pending_transactions} pending`,
        status: mandate.flagged_transactions > 5 ? "danger" :
               mandate.pending_transactions > 10 ? "warning" : "success",
        bgColor: getBgColorForStatus(
          mandate.flagged_transactions > 5 ? "danger" :
          mandate.pending_transactions > 10 ? "warning" : "success",
          index
        ),
        pendingCount: mandate.pending_transactions + mandate.flagged_transactions,
      }));
    } else {
      // Show summary stats if no individual mandates
      return [
        {
          initials: "AC",
          name: "Active Clients",
          description: `${metrics.activeMandates} total mandates`,
          status: metrics.activeMandates > 0 ? "success" : "warning",
          bgColor: getBgColorForStatus("success", 0),
          automationRate: metrics.automationRate,
        },
        {
          initials: "PR",
          name: "Pending Reviews",
          description: `${metrics.pendingReviews} transactions pending`,
          status: metrics.pendingReviews > 10 ? "warning" : "success",
          bgColor: getBgColorForStatus("warning", 1),
          pendingCount: metrics.pendingReviews,
        },
        {
          initials: "FL",
          name: "Flagged Items",
          description: `${metrics.flaggedTransactions} flagged transactions`,
          status: metrics.flaggedTransactions > 5 ? "danger" :
                 metrics.flaggedTransactions > 0 ? "warning" : "success",
          bgColor: getBgColorForStatus("danger", 2),
          pendingCount: metrics.flaggedTransactions,
        },
      ];
    }
  };

  const mandates = getMandatesData();

  const refresh = () => {
    refetchMandates();
    refetchMetrics();
  };

  return (
    <div className="bg-card p-6 rounded-2xl border">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-brand-primary">Mandate Health</h3>
          <RealtimeStatusIndicator variant="icon" size="sm" />
          {isLoading && (
            <Badge variant="secondary" className="text-xs animate-pulse">
              Updating...
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="btn-swiss-secondary h-7 px-2 py-1.5 rounded-[7px] text-[10px] leading-[10px] hover:bg-brand-secondary transition-colors"
            title="Refresh data"
          >
            <i className="fa-solid fa-refresh" />
          </button>
          <Button variant="ghost" size="sm" className="p-1.5 h-auto">
            <i className="fa-solid fa-ellipsis text-muted-foreground" />
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          // Loading skeletons
          Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex items-center">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="ml-3 flex-1">
                <Skeleton className="h-4 w-32 mb-1" />
                <Skeleton className="h-3 w-48" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-8 rounded" />
                <Skeleton className="w-3 h-3 rounded-full" />
              </div>
            </div>
          ))
        ) : mandates.length === 0 ? (
          // Empty state
          <div className="text-center py-8">
            <div className="text-muted-foreground">
              <i className="fa-solid fa-briefcase fa-2x mb-4" />
              <p className="text-lg font-medium mb-2">No Active Mandates</p>
              <p className="text-sm">
                Mandate information will appear here once you have active client portfolios.
              </p>
            </div>
          </div>
        ) : (
          // Real mandate data
          mandates.map((mandate, index) => (
            <div key={mandate.initials} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${mandate.bgColor}`}>
                {mandate.initials}
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-foreground">{mandate.name}</p>
                <p className="text-xs text-muted-foreground">
                  {mandate.description}
                  {mandate.automationRate && (
                    <span className="ml-1">
                      • {Math.round(mandate.automationRate)}% automated
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {mandate.pendingCount !== undefined && mandate.pendingCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {mandate.pendingCount}
                  </Badge>
                )}
                <div className={`w-3 h-3 rounded-full ${getStatusColor(mandate.status)}`} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};